import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import SessionFeedback from '../components/ora/Global/SessionFeedback';
import { RootState, store } from '../store/store';
import {
  getSessionFeedbackGlobalLastShown,
  getSessionFeedbackTypeLastShown,
  isTipDismissed,
  recordSessionFeedbackShown,
  type SessionFeedbackTypeKey,
} from '../helpers/messageStateGating';
import { updateCurrentUserInfo } from '../services/auth';
import { pendingFeedbackMilestoneCleared } from '../slices/todaySession/todaySessionSlice';
import { FeedbackTrigger } from '../types/Feedback';
import {
  SessionFeedbackContext,
  type SessionFeedbackOptions,
} from './SessionFeedbackContext';

interface SessionFeedbackProviderProps {
  children: React.ReactNode;
}

// Ordinal rank of the session-count milestones so the stamp only ever advances
// (a stale lower milestone must never overwrite a higher one). Non-milestone
// triggers rank 0.
const SESSION_MILESTONE_RANK: Partial<Record<FeedbackTrigger, number>> = {
  [FeedbackTrigger.SESSIONS_5]: 5,
  [FeedbackTrigger.SESSIONS_20]: 20,
  [FeedbackTrigger.SESSIONS_50]: 50,
};

const milestoneRank = (trigger?: FeedbackTrigger | null): number =>
  trigger ? (SESSION_MILESTONE_RANK[trigger] ?? 0) : 0;

const SessionFeedbackProvider: React.FC<SessionFeedbackProviderProps> = ({
  children,
}) => {
  const currentUser = useSelector((state: RootState) => state.user.profile);

  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );

  const dispatch = useDispatch();

  const pendingFeedbackMilestone = useSelector(
    (state: RootState) => state.todaySession.pendingFeedbackMilestone
  );

  const [isOpen, setIsOpen] = useState(false);

  const [feedbackType, setFeedbackType] = useState<'EXPERIENCE' | 'CONTENT'>(
    'EXPERIENCE'
  );

  const [contentType, setContentType] = useState<
    'QBANK' | 'FLASHCARDS' | 'VIDEOS' | undefined
  >(undefined);

  const open = useCallback(
    (options: SessionFeedbackOptions) => {
      // Global cooldown: no feedback of ANY type shown within the last 5 days.
      // Dual-read across the session-feedback prompt rows + legacy
      // lastSessionFeedbackDate.
      const globalLastShown = getSessionFeedbackGlobalLastShown(
        store.getState(),
        currentUser
      );

      if (
        globalLastShown &&
        moment().diff(moment(globalLastShown), 'days') < 5
      ) {
        return;
      }

      // Determine which type to check based on feedbackType and contentType
      let typeKey: SessionFeedbackTypeKey;
      if (options.feedbackType === 'EXPERIENCE') {
        typeKey = 'overall';
      } else {
        // CONTENT type
        if (options.contentType === 'QBANK') {
          typeKey = 'qbank';
        } else if (options.contentType === 'VIDEOS') {
          typeKey = 'videos';
        } else if (options.contentType === 'FLASHCARDS') {
          typeKey = 'flashcards';
        } else {
          // Fallback if contentType is not provided for CONTENT feedback
          return;
        }
      }

      // If the current user was created within the last 24 hours, don't open the modal
      if (
        currentUser?.createdAt &&
        moment(currentUser.createdAt).isAfter(moment().subtract(24, 'hours'))
      ) {
        return;
      }

      // Require the referral onboarding tip to have been seen first (dual-read).
      // Any version counts: users who cannot see the relaunched referral modal
      // (native, institutional) never get the new version recorded.
      if (
        !isTipDismissed(store.getState(), currentUser, 'promote-referral', {
          anyVersion: true,
        })
      ) {
        return;
      }

      // Per-type cooldown: no feedback of this type shown within the last 30 days.
      const typeLastShown = getSessionFeedbackTypeLastShown(
        store.getState(),
        currentUser,
        typeKey
      );

      if (typeLastShown && moment().diff(moment(typeLastShown), 'days') < 30) {
        return;
      }

      // Open the modal
      setFeedbackType(options.feedbackType);
      setContentType(options.contentType);
      setIsOpen(true);

      // Record the impression (normalized row + legacy dual-write). This is the
      // cooldown trigger, fired on display regardless of submit/dismiss.
      void recordSessionFeedbackShown(typeKey);

      // Stamp the originating trigger only now that the modal is actually
      // showing (past every cooldown gate above), so a milestone that was
      // blocked stays un-recorded and re-signals on the next completion
      // (ORA-196). Only advance — never let a stale lower milestone overwrite a
      // higher one already recorded (e.g. a delayed SESSIONS_5 after SESSIONS_20).
      if (
        options.feedbackTrigger &&
        milestoneRank(options.feedbackTrigger) >
          milestoneRank(currentUser?.lastFeedbackTrigger)
      ) {
        void updateCurrentUserInfo({
          lastFeedbackTrigger: options.feedbackTrigger,
        });
      }
    },
    [currentUser]
  );

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Consume a backend-flagged session-count milestone (ORA-196): attempt to open
  // EXPERIENCE feedback (subject to the same cooldown gates as any prompt) and
  // clear the transient signal. If a cooldown blocks it, `open` no-ops and the
  // trigger stays un-stamped, so the next completion re-signals it.
  useEffect(() => {
    if (!pendingFeedbackMilestone) {
      return;
    }

    open({
      feedbackType: 'EXPERIENCE',
      feedbackTrigger: pendingFeedbackMilestone,
    });

    dispatch(pendingFeedbackMilestoneCleared());
  }, [pendingFeedbackMilestone, open, dispatch]);

  // Close modal when user gets logged out
  useEffect(() => {
    if (!isAuthenticated && isOpen) {
      close();
    }
  }, [isAuthenticated, isOpen, close]);

  const value = useMemo(() => ({ open, close }), [open, close]);

  return (
    <SessionFeedbackContext.Provider value={value}>
      {children}
      <SessionFeedback
        open={isOpen}
        onClose={close}
        feedbackType={feedbackType}
        contentType={contentType}
      />
    </SessionFeedbackContext.Provider>
  );
};

export default SessionFeedbackProvider;
